import { useEffect, useRef, useState } from "react";
import styles from "./RowMenu.module.css";

/**
 * The `⋯` on a track row.
 *
 * Contents settled in docs/DESIGN-CONFLICTS.md D6 — toggle core/bonus, view rating history, point
 * at another recording. All three, and nothing else: this is a row menu, not a place to put
 * whatever a screen needs later.
 */

export interface HistoryEvent {
  value: number | null;
  previous: number | null;
  /** null on a first rating and on a clear: there is no delta from nothing. */
  delta: number | null;
  kind: string;
  note: string | null;
  at: string;
}

export interface RecordingOption {
  songId: number;
  title: string;
  discNo: number;
  trackNo: number | null;
  recordingMbid: string;
  isCurrent: boolean;
}

export interface RowMenuProps {
  songId: number;
  title: string;
  isCore: boolean;
  onToggleCore: (isCore: boolean) => void;
  loadHistory: (songId: number) => Promise<HistoryEvent[]>;
  loadRecordings: (songId: number) => Promise<RecordingOption[]>;
  /**
   * The wider search, for when the right recording is not on this record at all — the half of
   * §D6 the album's own release list cannot answer. Optional so the menu still works on screens
   * that have not wired it.
   */
  searchRecordings?: (q: string) => Promise<MbRecording[]>;
  onPointAt: (recordingMbid: string) => void;
}

export interface MbRecording {
  mbid: string;
  title: string;
  artist: string;
  lengthMs: number | null;
  releases: string[];
  disambiguation: string | null;
}

type Panel = "menu" | "history" | "recordings";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** `02 SEP 24`, explicit rather than via toLocaleDateString — ICU renders September as "Sept". */
const dateLabel = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

/** The sparkline appears at three or more events — below that it is a line through two dots. */
export const SPARK_AT = 3;

export function RowMenu({
  songId,
  title,
  isCore,
  onToggleCore,
  loadHistory,
  loadRecordings,
  searchRecordings,
  onPointAt,
}: RowMenuProps) {
  const [panel, setPanel] = useState<Panel | null>(null);
  const [history, setHistory] = useState<HistoryEvent[] | null>(null);
  const [recordings, setRecordings] = useState<RecordingOption[] | null>(null);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<MbRecording[] | null>(null);
  const [searching, setSearching] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // Escape and a click elsewhere both close it. A row menu that needs a second click on the same
  // button to dismiss is a menu you fight.
  useEffect(() => {
    if (panel === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setPanel(null);
      }
    };
    const onClick = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setPanel(null);
    };

    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onClick);
    };
  }, [panel]);

  const openHistory = async () => {
    setPanel("history");
    if (history === null) setHistory(await loadHistory(songId));
  };

  const openRecordings = async () => {
    setPanel("recordings");
    if (recordings === null) setRecordings(await loadRecordings(songId));
  };

  return (
    <div className={styles.root} ref={root}>
      <button
        type="button"
        className={styles.more}
        aria-label={`More actions for ${title}`}
        aria-expanded={panel !== null}
        data-testid="row-menu-button"
        onClick={() => setPanel((p) => (p === null ? "menu" : null))}
      >
        ⋯
      </button>

      {panel === "menu" && (
        <div className={styles.panel} data-testid="row-menu">
          <button
            type="button"
            className={styles.item}
            data-testid="menu-core"
            onClick={() => {
              onToggleCore(!isCore);
              setPanel(null);
            }}
          >
            {isCore ? "Move to bonus" : "Move to core"}
            <span className={styles.hint}>{isCore ? "STOPS COUNTING TOWARD THE SCORE" : "COUNTS TOWARD THE SCORE"}</span>
          </button>

          <button type="button" className={styles.item} data-testid="menu-history" onClick={() => void openHistory()}>
            Rating history
          </button>

          <button
            type="button"
            className={styles.item}
            data-testid="menu-recording"
            onClick={() => void openRecordings()}
          >
            Point at another recording
          </button>
        </div>
      )}

      {panel === "history" && (
        <div className={styles.panel} data-testid="history-panel">
          <div className={styles.panelTitle}>Rating history</div>
          {history === null ? (
            <div className={styles.empty}>LOADING</div>
          ) : history.length === 0 ? (
            // Never rated is a real state and says so, rather than showing an empty box.
            <div className={styles.empty} data-testid="history-empty">
              NEVER RATED
            </div>
          ) : (
            <>
              {history.length >= SPARK_AT && <Sparkline events={history} />}
              <div className={styles.events}>
                {history.map((event, i) => (
                  <div key={i} className={styles.event}>
                    <span className={styles.eventDate}>{dateLabel(event.at)}</span>
                    <span className={styles.eventValue} data-cleared={event.kind === "cleared" || undefined}>
                      {event.kind === "cleared" ? "CLEARED" : event.value}
                    </span>
                    {/* Vermilion up, error red down — and nothing at all on the first rating,
                        because there is no delta from nothing. */}
                    <span className={styles.delta} data-direction={event.delta === null ? undefined : event.delta > 0 ? "up" : "down"}>
                      {event.delta === null ? "" : event.delta > 0 ? `+${event.delta}` : event.delta}
                    </span>
                    <span className={styles.eventNote}>{event.note === "import" ? "IMPORTED" : ""}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <button type="button" className={styles.back} onClick={() => setPanel("menu")}>
            ← Back
          </button>
        </div>
      )}

      {panel === "recordings" && (
        <div className={styles.panel} data-testid="recordings-panel">
          <div className={styles.panelTitle}>Point at another recording</div>
          {recordings === null ? (
            <div className={styles.empty}>LOADING</div>
          ) : recordings.length === 0 ? (
            <div className={styles.empty} data-testid="recordings-empty">
              THIS ALBUM HAS NO PULLED TRACK LIST YET, SO THERE IS NOTHING TO POINT AT.
            </div>
          ) : (
            <div className={styles.events}>
              {recordings.map((option) => (
                <button
                  key={option.recordingMbid}
                  type="button"
                  className={styles.recording}
                  data-current={option.isCurrent || undefined}
                  data-testid="recording-option"
                  onClick={() => {
                    onPointAt(option.recordingMbid);
                    setPanel(null);
                  }}
                >
                  <span className={styles.recordingNo}>{option.trackNo ?? "—"}</span>
                  <span className={styles.recordingTitle}>{option.title}</span>
                  {option.isCurrent && <span className={styles.current}>CURRENT</span>}
                </button>
              ))}
            </div>
          )}
          {searchRecordings && (
            <div className={styles.search}>
              {/* The other half of §D6: this record's own release cannot offer a recording that
                  is not on it. One request per second, so it is a submit rather than a keystroke. */}
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (query.trim() === "") return;
                  setSearching(true);
                  void searchRecordings(query.trim())
                    .then(setFound)
                    .finally(() => setSearching(false));
                }}
              >
                <input
                  className={styles.searchInput}
                  value={query}
                  aria-label="Search MusicBrainz recordings"
                  placeholder="SEARCH ALL OF MUSICBRAINZ"
                  data-testid="recording-search"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </form>

              {searching && <div className={styles.empty}>LOOKING…</div>}
              {found !== null && !searching && found.length === 0 && (
                <div className={styles.empty} data-testid="recording-search-none">
                  NOTHING UNDER THAT NAME.
                </div>
              )}
              {found !== null && found.length > 0 && (
                <div className={styles.events} data-testid="recording-search-results">
                  {found.map((option) => (
                    <button
                      key={option.mbid}
                      type="button"
                      className={styles.recording}
                      data-testid="recording-found"
                      onClick={() => {
                        onPointAt(option.mbid);
                        setPanel(null);
                      }}
                    >
                      <span className={styles.recordingTitle}>{option.title}</span>
                      {/* The releases are usually the only way to tell a studio take from a live
                          one, so they travel with the row rather than behind a hover. */}
                      <span className={styles.recordingWhere}>
                        {[option.artist, option.disambiguation, ...option.releases].filter(Boolean).join(" · ")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={styles.note}>YOUR RATING IS NOT AFFECTED.</div>
          <button type="button" className={styles.back} onClick={() => setPanel("menu")}>
            ← Back
          </button>
        </div>
      )}
    </div>
  );
}

/** The rating over time, at the same 4px cell language the display strip uses. */
function Sparkline({ events }: { events: HistoryEvent[] }) {
  const values = events.map((e) => e.value).filter((v): v is number => v !== null);
  return (
    <div className={styles.spark} data-testid="history-spark" role="img" aria-label="Rating over time">
      {values.map((value, i) => (
        <div key={i} className={styles.sparkBar} style={{ height: `${4 + value * 2.4}px` }} />
      ))}
    </div>
  );
}
